import React, { useState, useEffect } from "react";
import {
  Button,
  Table,
  Space,
  Select,
  Input,
  Form,
  Row,
  Col,
  DatePicker,
  Card,
  Upload,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { Popconfirm } from "antd";
import { ErrorToast, SuccessToast } from "../../../../widgets/AppToast";
import { post } from "../../../../services/services";
import { ApiURL } from "../../../../services/apiConstants";

const { TextArea } = Input;
const { Option } = Select;

const JobRegisterTab = ({
  machineryData,
  functionCode,
  machineryid,
  code,
  shipId,
}) => {
  const [form] = Form.useForm();
  const selectedPeriod = Form.useWatch("period", form);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [loader, setLoader] = useState(false);
  const [editJob, setEditJob] = useState(null);
  const [viewOnly, setViewOnly] = useState(false);
  const [viewJobList, setviewJobList] = useState([]);
  const [list_roll_permission, set_list_roll_permission] = useState([]);
  const [errors, set_errors] = useState({});
  const userData = JSON.parse(localStorage.getItem("user_data"));
  const staffShipId = userData?.data?.ship_details?.ship_id;
  const activeShipId = staffShipId || shipId || machineryData?.ship_id;
  const companyId = userData?.data?.company?.company_id;

  const normalizeUpload = (event) => {
    if (Array.isArray(event)) {
      return event;
    }

    return event?.fileList || [];
  };

  const parsePartsValue = (value) => {
    if (Array.isArray(value)) {
      return value;
    }

    if (value === undefined || value === null || value === "") {
      return [];
    }

    if (typeof value === "string") {
      try {
        const parsedValue = JSON.parse(value);
        return Array.isArray(parsedValue) ? parsedValue : [value];
      } catch (error) {
        return [value];
      }
    }

    return [value];
  };

  const getRequiredParts = (partInUse, totalPrice, requiredPartsValue) => {
    const requiredParts = parsePartsValue(requiredPartsValue);

    if (requiredParts.length > 0 && typeof requiredParts[0] === "object") {
      return requiredParts.map((part) => ({
        parts_used: part.parts_in_use || part.parts_used || "",
        total_price: part.total_price || "",
      }));
    }

    const parts = parsePartsValue(partInUse);
    const prices = parsePartsValue(totalPrice);
    const rowCount = Math.max(parts.length, prices.length, 1);

    return Array.from({ length: rowCount }, (_, index) => ({
      parts_used: parts[index] || "",
      total_price: prices[index] || "",
    }));
  };


  const ListofRollPermissionStaff = () => {
    const FORM_DATA = new FormData();
    post(ApiURL.staff_list_role_permission, FORM_DATA).then((res) => {
      if (res?.status == true) {
        set_list_roll_permission(res?.role_permission_list);
      } else {
        const errors = res?.errors || {};
        set_errors(errors);
      }
    });
  };

  useEffect(() => {
    ListofRollPermissionStaff();
  }, []);

  useEffect(() => {
    const currentMachineryId =
      machineryid ||
      machineryData?.id ||
      machineryData?.machinery_id;

    if (!currentMachineryId) return;

    jobList();

    if (machineryData) {
      fetchJobs();
    }
  }, [machineryid, machineryData]);

  const fetchJobs = async () => {
    setLoader(true);
    setTimeout(() => {
      setJobs([]);
      setLoader(false);
    }, 500);
  };

  const handleAddJob = () => {
    setEditJob(null);
    setViewOnly(false);
    setIsFormVisible(true);

    let generatedCode = "";

    if (functionCode) {
      generatedCode = `${functionCode}`;
    }

    form.resetFields();
    form.setFieldsValue({
      code: generatedCode,
      ship_id: activeShipId,
      last_date_done: dayjs(),
      required_parts: [{ parts_used: "", total_price: "" }],
    });
  };

  const handleEditJob = async (record) => {
    setLoader(true);

    try {
      const FORM_DATA = new FormData();
      FORM_DATA.append("code", code);
      FORM_DATA.append("job_id", record.id);
      FORM_DATA.append(
        "machinery_id",
        machineryid || machineryData?.id || machineryData?.machinery_id
      );

      const res = await post(ApiURL.staff_view_job, FORM_DATA);

      if (res?.status === true) {
        const data = res?.response_data || res?.data;

        setEditJob(data);
        setViewOnly(false);
        setIsFormVisible(true);

        form.setFieldsValue({
          code: data?.code || "",
          title: data?.title || "",
          period: data?.frequency_type || "",
          frequency: data?.frequency_number || "",
          designation: String(data?.designation) || "",
          last_date_done: data?.last_date_done
            ? dayjs(data.last_date_done)
            : null,
          priority: data?.priority || "",
          job_description: data?.job_description || "",
          attachment: data?.attachment || null,
          counter_reading: data?.last_done_counter || "",
          counter_date: data?.last_date_done
            ? dayjs(data.last_date_done)
            : null,
          required_parts: getRequiredParts(
            record?.part_in_use,
            record?.total_price,
            record?.required_parts,
          ),
          parts_used: String(data?.part_in_use) || "",
          total_price: String(data?.total_price) || "",
        });
      }
    } catch (error) {
      ErrorToast("Error", "Something went wrong.");
    } finally {
      setLoader(false);
    }
  };

  const handleDeleteJob = async (id) => {
    setLoader(true);
    try {
      const FORM_DATA = new FormData();
      FORM_DATA.append("job_id", id);

      const res = await post(ApiURL.delete_job, FORM_DATA);

      if (res?.status) {
        SuccessToast("Success", "Job deleted successfully!");
        jobList();
      } else {
        ErrorToast("Error", res?.message || "Failed to delete job.");
      }
    } catch (error) {
      console.log("deleteJobError", error);
      ErrorToast("Error", "Something went wrong while deleting job.");
    } finally {
      setLoader(false);
    }
  };

  const onFinish = async (values) => {
    setLoader(true);

    try {
      const FORM_DATA = new FormData();

      const requiredParts = (values.required_parts || []).map((part) => ({
        parts_in_use: part.parts_used,
        total_price: part.total_price,
      }));

      FORM_DATA.append("code", values.code);
      FORM_DATA.append("title", String(values.title || ""));
      FORM_DATA.append(
        "machinery_id",
        machineryid || machineryData?.id || machineryData?.machinery_id
      );

      FORM_DATA.append("frequency_type", values.period || "");
      FORM_DATA.append("frequency_number", values.frequency || "");

      FORM_DATA.append("designation", String(values.designation || ""));
      FORM_DATA.append("priority", values.priority || "");
      FORM_DATA.append("job_description", values.job_description || "");

      FORM_DATA.append(
        "last_date_done",
        values.period === "hourly"
          ? values.counter_date
            ? dayjs(values.counter_date).format("YYYY-MM-DD")
            : ""
          : values.last_date_done
            ? dayjs(values.last_date_done).format("YYYY-MM-DD")
            : ""
      );

      FORM_DATA.append("last_done_counter", values.counter_reading || "");

      FORM_DATA.append(
        "counter_date",
        values.counter_date
          ? dayjs(values.counter_date).format("YYYY-MM-DD")
          : ""
      );

      FORM_DATA.append("required_parts", JSON.stringify(requiredParts));

      (values.attachment || []).forEach((file) => {
        if (file?.originFileObj) {
          FORM_DATA.append("attachment", file.originFileObj);
        }
      });

      let res;

      if (editJob) {
        FORM_DATA.append("job_id", editJob.id || editJob.job_id);
        FORM_DATA.append(
          "machinery_id",
          machineryid || machineryData?.id || machineryData?.machinery_id
        );

        res = await post(ApiURL.staff_edit_job, FORM_DATA);

        if (res?.status) {
          SuccessToast("Success", "Job updated successfully!");
        } else {
          ErrorToast("Error", res?.message || "Update failed");
          return;
        }
      } else {
        console.log("Add Job FORM_DATA", Object.fromEntries(FORM_DATA.entries()));
        res = await post(ApiURL.staff_add_job, FORM_DATA);

        if (res?.status) {
          SuccessToast("Success", "Job added successfully!");
        } else {
          ErrorToast("Error", res?.message || "Add failed");
          return;
        }
      }

      await jobList();
      setIsFormVisible(false);
      setEditJob(null);
      form.resetFields();

    } catch (error) {
      console.log(error);
      ErrorToast("Error", "Something went wrong.");
    } finally {
      setLoader(false);
    }
  };

  const jobList = async () => {
    setLoader(true);
    try {
      const formData = new FormData();
      formData.append("code", code);
      formData.append("machinery_id", machineryid || machineryData?.id || machineryData?.machinery_id);
      if (companyId) formData.append("company_id", companyId);
      if (activeShipId) formData.append("ship_id", activeShipId);


      const res = await post(ApiURL.staff_list_job, formData);

      if (res?.status) {
        setviewJobList(res?.data || []);
      }
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoader(false);
    }
  };

  const viewjob = async (id) => {
    setLoader(true);

    try {
      const FORM_DATA = new FormData();
      FORM_DATA.append("job_id", id);

      const res = await post(ApiURL.staff_view_job, FORM_DATA);

      if (res?.status === true) {
        const data = res?.response_data || res?.data;

        setEditJob(data);
        setViewOnly(true);
        setIsFormVisible(true);

        form.setFieldsValue({
          code: data?.code || "",
          period: data?.frequency_type || "",
          frequency: data?.frequency_number || "",
          designation: String(data?.designation) || "",
          last_date_done: data?.last_date_done
            ? dayjs(data.last_date_done)
            : null,
          priority: data?.priority || "",
          job_description: data?.job_description || "",
          attachment: [],
          counter_reading: data?.last_done_counter || "",
          counter_date: data?.last_date_done
            ? dayjs(data.last_date_done)
            : null,
          required_parts: getRequiredParts(
            data?.part_in_use,
            data?.total_price,
            data?.required_parts,
          ),
          title: String(data?.title) || ""
        });
      } else {
        ErrorToast("Error", res?.message || "Unable to fetch job details.");
      }
    } catch (error) {
      console.log("viewJobError", error);
      ErrorToast("Error", "Something went wrong while fetching job details.");
    } finally {
      setLoader(false);
    }
  };

  const columns = [
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      align: "center",
    },
    {
      title: "Job Title",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "Period",
      dataIndex: "frequency_type",
      key: "frequency_type",
      align: "center",
    },
    {
      title: "Frequency",
      dataIndex: "frequency_number",
      key: "frequency_number",
      align: "center",
    },
    {
      title: "Designation",
      dataIndex: "designation",
      key: "designation",
      align: "center",
    },
    {
      title: "Last Date Done",
      dataIndex: "last_date_done",
      key: "last_date_done",
      align: "center",
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      align: "center",
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (_, record) => (
        <Space
          size="small"
          style={{ display: "flex", justifyContent: "center" }}
        >
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined style={{ fontSize: "16px" }} />}
            onClick={() => viewjob(record.id)}
            style={{
              backgroundColor: "#D91D3B",
              borderColor: "#D91D3B",
              width: "32px",
              height: "32px",
              borderRadius: "6px",
            }}
          />

          <Button
            type="primary"
            size="small"
            icon={<EditOutlined style={{ fontSize: "16px" }} />}
            onClick={() => handleEditJob(record)}
            style={{
              backgroundColor: "#D91D3B",
              borderColor: "#D91D3B",
              width: "32px",
              height: "32px",
              borderRadius: "6px",
            }}
          />

          <Popconfirm
            title="Are you sure you want to delete this job?"
            description="This action cannot be undone."
            okText="Delete"
            cancelText="Cancel"
            onConfirm={() => handleDeleteJob(record.id)}
          >
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined style={{ fontSize: "16px" }} />}
              style={{
                backgroundColor: "#D91D3B",
                borderColor: "#D91D3B",
                width: "32px",
                height: "32px",
                borderRadius: "6px",
              }}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (isFormVisible) {
    return (
      <div style={{ marginTop: "10px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "20px",
          }}
        >
          <h4 style={{ margin: 0 }}>
            {viewOnly ? "View Job" : editJob ? "Edit Job" : "Add Job"}
          </h4>

          <Button onClick={() => setIsFormVisible(false)}>Back to List</Button>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          disabled={viewOnly}
        >
          <Card
            title={<span style={{ fontWeight: 600 }}>General</span>}
            style={{
              marginBottom: "20px",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            }}
          >
            <Row gutter={[24, 16]}>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name="code"
                  label="Job Code"
                  rules={[{ required: true, message: "Please enter code" }]}
                >
                  <Input placeholder="Enter code" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name="title"
                  label="Job Title"
                  rules={[{ required: true, message: "Please enter Job Title" }]}
                >
                  <Input placeholder="Enter Job Title" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name="period"
                  label="Period"
                  rules={[{ required: true, message: "Please select period" }]}
                >
                  <Select placeholder="Select period" disabled={editJob}>
                    <Option value="hourly">Running Hours</Option>
                    <Option value="daily">Day</Option>
                    <Option value="weekly">Week</Option>
                    <Option value="monthly">Month</Option>
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name="frequency"
                  label="Frequency"
                  rules={[
                    { required: true, message: "Please enter frequency" },
                  ]}
                >
                  <Input placeholder="Enter frequency" disabled={editJob} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name="designation"
                  label="Designation"
                  rules={[
                    { required: true, message: "Please enter designation" },
                  ]}
                >
                  <Select placeholder="--- Select Designation ---" style={{ width: "100%" }}>
                    {list_roll_permission
                      ?.filter((role) => role.status && !role.soft_delete)
                      .map((role) => (
                        <Select.Option key={role.id} value={role.title}>
                          {role.title}
                        </Select.Option>
                      ))}
                  </Select>
                </Form.Item>
              </Col>

              {selectedPeriod !== "hourly" && (
                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    name="last_date_done"
                    label="Last Updated At"
                    rules={[
                      { required: true, message: "Please select last date" },
                    ]}
                  >
                    <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
                  </Form.Item>
                </Col>
              )}

              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name="priority"
                  label="Priority"
                  rules={[
                    { required: true, message: "Please select priority" },
                  ]}
                >
                  <Select placeholder="Select Priority">
                    <Option value="High">High</Option>
                    <Option value="Medium">Medium</Option>
                    <Option value="Low">Low</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Card
            title={<span style={{ fontWeight: 600 }}>Job Description</span>}
            style={{
              marginBottom: "20px",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            }}
          >
            <Row gutter={24}>
              <Col span={24}>
                <Form.Item
                  name="job_description"
                  label="Description"
                  rules={[
                    {
                      required: true,
                      message: "Please enter job description",
                    },
                  ]}
                >
                  <TextArea
                    rows={4}
                    placeholder="Enter description details..."
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Card
            title={<span style={{ fontWeight: 600 }}>Job Attachment</span>}
            style={{
              marginBottom: "20px",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            }}
          >
            <Row gutter={24}>
              <Col span={24}>
                <Form.Item
                  name="attachment"
                  valuePropName="fileList"
                  getValueFromEvent={normalizeUpload}
                >
                  <Upload multiple beforeUpload={() => false}>
                    <Button icon={<UploadOutlined />}>
                      Click to Upload Attachment
                    </Button>
                  </Upload>
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {selectedPeriod === "hourly" && (
            <Card
              title={<span style={{ fontWeight: 600 }}>Job Counter</span>}
              style={{
                marginBottom: "20px",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              }}
            >
              <Row gutter={[24, 16]}>
                <Col xs={24} sm={12} md={12}>
                  <Form.Item
                    name="counter_reading"
                    label="Counter Reading"
                    rules={[
                      {
                        required: true,
                        message: "Please enter counter reading",
                      },
                    ]}
                  >
                    <Input type="number" placeholder="Enter counter reading" />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={12}>
                  <Form.Item
                    name="counter_date"
                    label="Date"
                    rules={[
                      {
                        required: true,
                        message: "Please enter counter date",
                      },
                    ]}
                  >
                    <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          )}

          <Card
            title={<span style={{ fontWeight: 600 }}>Required Parts</span>}
            style={{
              marginBottom: "20px",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            }}
          >
            <Form.List
              name="required_parts"
              initialValue={[{ parts_used: "", total_price: "" }]}
            >
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Row gutter={[24, 16]} key={key} align="middle">
                      <Col xs={24} sm={12} md={11}>
                        <Form.Item
                          {...restField}
                          name={[name, "parts_used"]}
                          label="Parts in Used"
                          rules={[
                            {
                              required: false,
                              message: "Please enter parts in used",
                            },
                          ]}
                        >
                          <Input type="text" placeholder="Enter Parts Used" />
                        </Form.Item>
                      </Col>

                      <Col xs={24} sm={12} md={11}>
                        <Form.Item
                          {...restField}
                          name={[name, "total_price"]}
                          label="Total Price"
                          rules={[
                            {
                              required: false,
                              message: "Please enter total price",
                            },
                          ]}
                        >
                          <Input type="text" placeholder="Enter Total Price" />
                        </Form.Item>
                      </Col>

                      {!viewOnly && fields.length > 1 && (
                        <Col xs={24} sm={24} md={2}>
                          <Button
                            danger
                            type="text"
                            icon={<DeleteOutlined />}
                            onClick={() => remove(name)}
                            style={{ marginTop: "6px" }}
                          />
                        </Col>
                      )}
                    </Row>
                  ))}

                  {!viewOnly && (
                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={() => add({ parts_used: "", total_price: "" })}
                    >
                      Add More
                    </Button>
                  )}
                </>
              )}
            </Form.List>
          </Card>

          {!viewOnly && (
            <div
              style={{
                textAlign: "center",
                marginTop: "20px",
                marginBottom: "20px",
              }}
            >
              <Button
                type="primary"
                size="large"
                htmlType="submit"
                loading={loader}
                className="save_btn"
                style={{
                  height: "48px",
                  padding: "0 60px",
                  borderRadius: "24px",
                  fontWeight: 600,
                }}
              >
                {editJob ? "Update Job" : "Save Job"}
              </Button>
            </div>
          )}
        </Form>
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "15px",
        }}
      >
        <h4 style={{ margin: 0 }}>Job Register</h4>

        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAddJob}
          className="save_btn"
        >
          Add Job
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={viewJobList}
        loading={loader}
        rowKey="id"
        bordered
        pagination={{ pageSize: 10 }}
        className="custom-table"
      />
    </div>
  );
};

export default JobRegisterTab;
